import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import logo from "../img/logo_white.png";
import { Button, Card, Form, Input, Typography } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import "../pages/Login.css";
import Footer from "../components/footer/Footer";
import LanguageMenu from "../components/languageMenu/LanguageMenu";
import { auth } from "../firebase";
import { sendEmailVerification, sendPasswordResetEmail, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { isValidEmail } from "../utils/authValidation";

export default function Login(props) {
  const { authenticateUser } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [infoMessage, setInfoMessage] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const trimmedEmail = email.trim();
  const hasValidEmail = isValidEmail(trimmedEmail);
  const showInvalidEmail = trimmedEmail.length > 0 && !hasValidEmail;

  useEffect(() => {
    props.funcNav(false);
  }, [props]);

  useEffect(() => {
    if (searchParams.get("verifyEmail") === "1") {
      setInfoMessage(
        t("signup.verificationEmailSent", {
          defaultValue: "Check your inbox and verify your email before logging in.",
        })
      );
      setSearchParams({}, { replace: true });
    }
    if (searchParams.get("verifyEmailRetry") === "1") {
      setInfoMessage(
        t("signup.verificationSendRetry", {
          defaultValue: "Account created. Sign in once to send the verification email again.",
        })
      );
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrorMessage(undefined);
    name === "email" ? setEmail(value) : setPassword(value);
  };

  const logInUser = async () => {
    if (!trimmedEmail || !password) {
      setErrorMessage(
        t("login.fillEmailAndPassword", {
          defaultValue: "Please enter your email and password.",
        })
      );
      return;
    }

    if (!hasValidEmail) {
      setErrorMessage(
        t("login.invalidEmail", {
          defaultValue: "Please enter a valid email address.",
        })
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(undefined);
    setInfoMessage(undefined);
    try {
      const userCred = await signInWithEmailAndPassword(auth, trimmedEmail, password);
      if (!userCred.user.emailVerified) {
        try {
          await sendEmailVerification(userCred.user, {
            url: `${window.location.origin}/login`,
            handleCodeInApp: false,
          });
        } catch (verificationError) {
          console.error("Verification retry error:", verificationError);
        }
        await signOut(auth);
        setInfoMessage(
          t("signup.verificationEmailSent", {
            defaultValue: "Check your inbox and verify your email before logging in.",
          })
        );
        return;
      }

      const session = await authenticateUser({ forceRefresh: true });
      if (!session?.fleetId) {
        setErrorMessage(
          t("login.noFleetAccess", {
            defaultValue: "We could not find an active fleet for this account.",
          })
        );
        return;
      }
      const redirectParam = searchParams.get("redirect");
      const redirectPath = redirectParam ? decodeURIComponent(redirectParam) : "/welcome";
      navigate(redirectPath);
      props.funcNav(true);
    } catch (error) {
      console.error(error);
      setErrorMessage(t("login.invalidpassword"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!trimmedEmail) return;

    if (!hasValidEmail) {
      setErrorMessage(
        t("login.invalidEmail", {
          defaultValue: "Please enter a valid email address.",
        })
      );
      return;
    }

    setIsResettingPassword(true);
    setErrorMessage(undefined);
    setInfoMessage(undefined);
    try {
      await sendPasswordResetEmail(auth, trimmedEmail, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
    } catch (error) {
      const code = error?.code || "";
      if (code !== "auth/user-not-found") {
        console.error("Password reset error:", error);
      }
    } finally {
      setIsResettingPassword(false);
      setInfoMessage(
        t("login.resetPasswordSent", {
          defaultValue:
            "If you have an account with that email, a recovery email was sent. The reset link expires in 1 hour.",
        })
      );
    }
  };

  const handleSubmit = async () => {
    logInUser();
  };

  const canSubmit = Boolean(trimmedEmail && password && hasValidEmail);

  return (
    <div>
      <LanguageMenu />
      <div className="login-page">
        <div className="login-container">
          <Typography.Title level={2} className="Gandolapp">
            <img src={logo} alt="logo" />
          </Typography.Title>
          <Card className="login-card">
            <Form layout="vertical" onFinish={handleSubmit}>
              <div className="auth-validation-slot">
                <Form.Item validateStatus={showInvalidEmail ? "error" : ""}>
                  <Input
                    type="email"
                    name="email"
                    placeholder={t("login.email")}
                    prefix={<UserOutlined />}
                    value={email}
                    onChange={handleChange}
                  />
                </Form.Item>
                <div className={`auth-validation-message${showInvalidEmail ? " is-visible" : ""}`}>
                  {showInvalidEmail
                    ? t("login.invalidEmail", {
                        defaultValue: "Please enter a valid email address.",
                      })
                    : "\u00A0"}
                </div>
              </div>
              <Form.Item>
                <Input.Password
                  name="password"
                  placeholder={t("login.password")}
                  prefix={<LockOutlined />}
                  value={password}
                  onChange={handleChange}
                />
                <div className="login-actions">
                  <Button
                    type="link"
                    className="login-link-button"
                    onClick={handleForgotPassword}
                    disabled={!trimmedEmail || isResettingPassword}
                    loading={isResettingPassword}
                  >
                    {t("login.forgotpassword")}
                  </Button>
                  {errorMessage && <p className="error-Message">{errorMessage}</p>}
                </div>
                {infoMessage && <p className="info-Message">{infoMessage}</p>}
              </Form.Item>
              <Button type="primary" htmlType="submit" block disabled={!canSubmit} loading={isSubmitting}>
                {t("login.login")}
              </Button>
              <div className="login-footer">
                <a href="/signup">{t("login.signup")}</a>
              </div>
            </Form>
          </Card>
        </div>
      </div>
      <Footer variant="plain" />
    </div>
  );
}
